import { inject, Injectable } from '@angular/core';
import { FeatureFlagsService } from '@coin/shared/core/data-access';
import { PermissionAction, PermissionResource } from '@coin/shared/core/util-enums';
import { NavigationLink, RouteBuilder, RouteTree, TreeNode } from '@coin/shared/core/util-helpers';
import { CoinTab, RBAF_PERMISSION_RESOURCES } from '@coin/shared/core/util-models';
import { Store } from '@ngxs/store';
import { UserState } from './user.state';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private store = inject(Store);
  private featureFlagsService = inject(FeatureFlagsService);
  private userState = inject(UserState);

  isHead = toSignal(this.userState.currentUserDetails$.pipe(map(details => details?.isHead)));

  public isSuperAdmin(): boolean {
    return this.hasPermissions(PermissionResource.All);
  }

  public isSuperAdminBasedOnRbaf(): boolean {
    return this.hasViewAccess(PermissionResource.All);
  }

  public isOrgPlanningAdmin(): boolean {
    return this.hasPermissions(PermissionResource.OrganisationPlanningAdmin);
  }

  public isHeadcountApprovalAdmin(): boolean {
    return this.hasPermissions(PermissionResource.HeadcountApprovalAdmin);
  }

  public isHeadcountController(): boolean {
    return this.hasPermissions(PermissionResource.HeadcountController);
  }

  public isStandingPeHr(): boolean {
    return (
      this.hasPermissions(PermissionResource.StandingPositionEvaluationHR) && !this.isSuperAdmin() && !this.isCAndB()
    );
  }

  /*
   @returns `true` if the user has any permission AND any action on that permission, otherwise `false`
  public hasPermissionsAndActions({ resources, actions }: { resources: PermissionResource[]; actions: PermissionAction[] }): boolean {
    if (this.isSuperAdmin()) return true;
    if (this.hasViewAccess(...resources)) return true; // RBAF view access counts as all actions

    const userPermissions = this.store.selectSnapshot(UserState.allPermissions);
    return resources.some(resource => {
      const userResource = userPermissions.get(resource);
      if (!userResource) return false;
      if (userResource.httpActions.has(PermissionAction.All)) return true;
      if (!actions?.length) return true;
      return actions.some(action => userResource.httpActions.has(action));
    });
  }
  */

  // Intermediate solution for permission split currently in place
  /** @returns `true` if the user has any permission AND any action on that permission, otherwise `false` */
  public hasPermissionsAndActions({
    resources,
    actions
  }: {
    resources: PermissionResource[];
    actions: PermissionAction[];
  }): boolean {
    if (this.isSuperAdmin() || this.isSuperAdminBasedOnRbaf()) return true;

    const rbafResources = new Set(RBAF_PERMISSION_RESOURCES);
    const meritResources = resources.filter(res => rbafResources.has(res));
    const otherResources = resources.filter(res => !rbafResources.has(res) || res === PermissionResource.Emulation);
    const hasMeritResources = meritResources.length > 0;
    const hasOtherResources = otherResources.length > 0;

    if (!hasMeritResources && !hasOtherResources) {
      return false;
    }

    const userPermissions = this.store.selectSnapshot(UserState.allPermissions);
    const hasPermissionResourceActions = otherResources.some(resource => {
      const userResource = userPermissions.get(resource);
      if (!userResource) return false;
      if (userResource.httpActions.has(PermissionAction.All)) return true;
      if (!actions?.length) return true;
      return actions.some(action => userResource.httpActions.has(action));
    });

    const hasMeritPermissions = hasMeritResources ? this.hasViewAccess(...meritResources) : false; // RBAF view access counts as all actions
    const hasOtherPermissions = hasOtherResources
      ? hasPermissionResourceActions || this.hasViewAccess(...otherResources)
      : false;

    return hasMeritPermissions || hasOtherPermissions;
  }

  /*
  /!** @returns `true` if the user has any permission, otherwise `false` *!/
  public hasPermissions(...resources: PermissionResource[]): boolean {
    return this.hasPermissionResource(...resources) || this.hasViewAccess(...resources);
  }
  */

  // Intermediate solution for permission split currently in place
  /** @returns `true` if the user has any permission, otherwise `false`.
   * Merit resources are handled by RBAF endpoint groups, other resources are still handled on role permissions
   * */
  public hasPermissions(...resources: PermissionResource[]): boolean {
    const rbafResources = new Set(RBAF_PERMISSION_RESOURCES);
    const meritResources = resources.filter(res => rbafResources.has(res));
    const otherResources = resources.filter(res => !rbafResources.has(res) || res === PermissionResource.Emulation);
    const hasMeritResources = meritResources.length > 0;
    const hasOtherResources = otherResources.length > 0;

    if (!hasMeritResources && !hasOtherResources) {
      return false;
    }

    const hasMeritPermissions = hasMeritResources ? this.hasViewAccess(...meritResources) : false;
    const hasOtherPermissions = hasOtherResources
      ? this.hasPermissionResource(...otherResources) || this.hasViewAccess(...otherResources)
      : false;
    return hasMeritPermissions || hasOtherPermissions;
  }

  // permissions based on role permission resources
  private hasPermissionResource(...resources: PermissionResource[]): boolean {
    const userPermissions = this.store.selectSnapshot(UserState.allPermissions);
    return userPermissions?.has(PermissionResource.All) || resources.some(resource => userPermissions?.has(resource));
  }

  // permission based on endpoint groups (RBAF)
  private hasViewAccess(...resources: PermissionResource[]): boolean {
    const accessViews = this.store.selectSnapshot(UserState.accessViews);
    return accessViews?.has(PermissionResource.All) || resources.some(accessView => accessViews?.has(accessView));
  }

  public isCAndB(): boolean {
    return this.hasPermissions(PermissionResource.CAndB);
  }

  public isHeadcountApprovalHr(): boolean {
    return this.hasPermissions(PermissionResource.HeadcountApprovalHr);
  }

  public isHeadcountApprovalUser(): boolean {
    return this.hasPermissions(PermissionResource.HeadcountApprovalUser);
  }

  public isGlobalHrBp(): boolean {
    return this.userState.currentUserDetails()?.isGlobalHrBp;
  }

  // region Navigation
  getNavigationLinks(url: URL, routerBuilder: RouteBuilder<RouteTree>): NavigationLink[] {
    const { navigationRoot, firstSegment } = routerBuilder.findNavigationRoot(url);
    if (!navigationRoot) {
      console.error(`No navigation-root found for ${firstSegment} module!`);
      return [];
    }

    return Object.keys(navigationRoot.children)
      .map(selector => navigationRoot.children[selector] as TreeNode)
      .filter(node => this.isRoutePermitted(node) && !node.hiddenNavigation)
      .filter(
        node =>
          !node?.isVisible ||
          Object.entries(node.isVisible).every(([param, validValues]) =>
            validValues.includes(url.searchParams.get(param))
          )
      )
      .map(node => {
        return {
          title:
            this.resolveTitle(url, node) ??
            this.resolveTitle(
              url,
              Object.values(node.children || {}).find(child => child.segment === '')
            ),
          hasChilds: Object.values(node.children || {}).some(child => child.segment !== ''),
          tooltip: node.tooltip,
          disabledTooltip: node.disabledTooltip,
          url: `/${firstSegment}/${node.extensionPrefix || ''}${node.segment}`,
          preserveParams: node.params,
          icon: node.icon,
          permissions: node.permissions,
          controlledByFeatureFlag: node.controlledByFeatureFlag
        };
      });
  }

  private isRoutePermitted(node: TreeNode): boolean {
    if (node.controlledByFeatureFlag && this.featureFlagsService.isInactive(node.controlledByFeatureFlag)) {
      return false;
    }

    if (node.permissions?.length > 0 && !this.hasPermissions(...node.permissions)) {
      return false;
    }

    return true;
  }

  public getSubPageTabs(url: URL, routerBuilder: RouteBuilder<RouteTree>): CoinTab[] {
    const { navigationRoot, firstSegment } = routerBuilder.findNavigationRoot(url);
    if (!navigationRoot) {
      console.error(`No navigation-root found for ${firstSegment} module!`);
      return [];
    }

    const parentRoute = `${firstSegment}${navigationRoot.segment ? `/${navigationRoot.segment}` : ''}`;
    const disabledTabTooltip = 'general.disabled-nav-tab';
    return Object.keys(navigationRoot.children)
      .map(selector => navigationRoot.children[selector] as TreeNode)
      .filter(node => url.pathname.slice(1).startsWith(`${parentRoute}${node.segment ? `/${node.segment}` : ''}`))
      .filter(node => this.isRoutePermitted(node))
      .flatMap(node => {
        const childs = Object.values(node.children || {});
        const hasEmptyRoute = childs.some(child => child.segment === '');
        if (!childs.length) {
          return [];
        }

        const baseRoute = `${parentRoute}${node.segment ? `/${node.segment}` : ''}`;
        let tabs = childs
          .filter(child => this.isRoutePermitted(child))
          .filter(
            child =>
              !child?.isVisible ||
              Object.entries(child.isVisible).every(([param, validValues]) =>
                validValues.includes(url.searchParams.get(param))
              )
          )
          .map(
            child =>
              ({
                name: child.title,
                isDisabled: !!child.paramTitle && !url.searchParams.has(child.paramTitle),
                isSelected: url.pathname === `${baseRoute}${child.segment ? `/${child.segment}` : ''}`,
                route: `${baseRoute}${child.segment ? `/${child.segment}` : ''}`,
                tooltip: disabledTabTooltip
              }) as CoinTab
          );

        if (!hasEmptyRoute && !node.emptyPage) {
          tabs = [
            {
              name: this.resolveTitle(url, node),
              isDisabled: false,
              isSelected: url.pathname.endsWith(node.segment),
              route: baseRoute,
              tooltip: disabledTabTooltip
            },
            ...tabs
          ];
        }

        return tabs.length === 1 ? [] : tabs;
      });
  }

  private resolveTitle(url: URL, node: TreeNode): string {
    if (!node?.title || typeof node.title === 'string') {
      return node.title as string;
    }

    const key = Object.keys(node.title)[0];
    const value = url.searchParams.get(key);
    return node.title[key][value];
  }

  // endregion
}
